import { Box, Button, Grid } from "@mui/material";
import {
  StudiesGetStudyByIdStudyModel,
  StudiesUpdateStudyForm,
  UsersGetAllUsersUserModel,
} from "api";
import { useForm } from "react-hook-form";
import BackButtonComponent from "shared/components/BackButton";
import DropDownField from "shared/forms/fields/DropDownField";
import TextField from "shared/forms/fields/TextField";
import FormErrorAlert from "shared/forms/FormErrorAlert";
import SubmitButton from "shared/forms/SubmitButton";
import withFormStatus from "shared/forms/withFormStatus";
import StickyFooter from "shared/layout/StickyFooter";

import DeleteStudyForm from "./DeleteStudyForm";

interface Props {
  users: UsersGetAllUsersUserModel[];
  study: StudiesGetStudyByIdStudyModel;
  onSave(form: StudiesUpdateStudyForm): void;
  onSync(study: StudiesGetStudyByIdStudyModel): void;
}

function EditStudyForm({ study, onSave, users, onSync }: Props) {
  const { control, handleSubmit, formState } = useForm<StudiesUpdateStudyForm>({
    defaultValues: study,
    mode: "onChange",
  });

  const onSubmit = handleSubmit((form) => onSave(form));

  return (
    <Box onSubmit={onSubmit} component="form">
      <FormErrorAlert />
      <Grid container spacing={4}>
        <Grid item xs={4}>
          <TextField
            rules={{
              required: true,
            }}
            control={control}
            name="name"
            label="Study Name"
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            control={control}
            rules={{
              required: true,
            }}
            name="referenceId"
            label="CC Id"
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            control={control}
            name="siteId"
            label="Site Id"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            control={control}
            name="description"
            label="Description"
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <DropDownField
            rules={{
              required: true,
            }}
            options={users}
            getOptionKey={(opt) => opt.id}
            getOptionLabel={(opt) => `${opt.name?.first} ${opt.name?.last}`}
            control={control}
            name="coordinatorId"
            label="Coordinator"
            required
          />
        </Grid>
        <Grid item xs={4}>
          <DropDownField
            rules={{
              required: true,
            }}
            options={users}
            getOptionKey={(opt) => opt.id}
            getOptionLabel={(opt) => `${opt.name?.first} ${opt.name?.last}`}
            control={control}
            name="principalInvestigatorId"
            label="Principal Investigator"
            required
          />
        </Grid>
        <Grid item xs={4}>
          <DropDownField
            options={users}
            getOptionKey={(opt) => opt.id}
            getOptionLabel={(opt) => `${opt.name?.first} ${opt.name?.last}`}
            control={control}
            name="researchAssistantId"
            label="Research Assistant"
          />
        </Grid>
        <Grid item xs={6}>
          <DropDownField
            rules={{
              required: true,
            }}
            options={users}
            getOptionKey={(opt) => opt.id}
            getOptionLabel={(opt) => `${opt.name?.first} ${opt.name?.last}`}
            control={control}
            name="labManagerId"
            label="Lab Manager"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            rules={{
              required: true,
            }}
            control={control}
            name="labEmail"
            label="Lab Email"
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            control={control}
            name="labVendor1"
            label="Lab Vendor 1"
            fullWidth
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            control={control}
            name="labVendor2"
            label="Lab Vendor 2"
            fullWidth
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            control={control}
            name="labVendor3"
            label="Lab Vendor 3"
            fullWidth
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            control={control}
            name="labVendor4"
            label="Lab Vendor 4"
            fullWidth
          />
        </Grid>
      </Grid>
      <StickyFooter>
        <Button
          sx={{ margin: "0 .25rem" }}
          onClick={() => {
            onSync(study);
          }}
        >
          Sync Study
        </Button>
        <BackButtonComponent to="/studies" />
        <DeleteStudyForm study={study} />
        <SubmitButton variant="save" disabled={!formState.isValid} />
      </StickyFooter>
    </Box>
  );
}

export default withFormStatus(EditStudyForm);
